import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { describeBillingStatus, formatBillingDate } from "@/lib/billing-status";

export interface BillingOverview {
  summary: {
    billingStatus: string | null;
    billedSeats: number | null;
    billableSeats: number;
    reviewers: number;
    requiresReconciliation: boolean;
    graceEndsAt: number | null;
    state: string;
  };
  billing: {
    provider?: string;
    providerCustomerId?: string;
    providerSubscriptionId?: string;
    collectionMethod?: "charge_automatically" | "send_invoice";
    cancelAtPeriodEnd?: boolean;
    currentPeriodEnd?: number;
    daysUntilDue?: number;
    latestInvoice?: {
      invoiceId: string;
      status: string;
      number?: string;
      hostedInvoiceUrl?: string;
      dueAt?: number;
    };
  } | null;
  entitlement: { expiresAt?: number } | null;
}

const LIVE_SUBSCRIPTION_STATUSES = ["active", "trialing", "past_due", "unpaid"];

const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: "being prepared",
  open: "unpaid",
  paid: "paid",
  uncollectible: "uncollectible",
  void: "void",
};

function InvoicedBillingNotice(props: { billing: NonNullable<BillingOverview["billing"]> }) {
  const { daysUntilDue, latestInvoice } = props.billing;
  const dueDate =
    latestInvoice?.dueAt && latestInvoice.status === "open" ? `, due ${formatBillingDate(latestInvoice.dueAt)}` : "";
  return (
    <p className="text-sm text-muted-foreground">
      Billed by invoice{daysUntilDue !== undefined ? `, payable within ${daysUntilDue} days` : ""}.
      {latestInvoice ? (
        <>
          {" "}Latest invoice {latestInvoice.number ?? latestInvoice.invoiceId} is{" "}
          {INVOICE_STATUS_LABELS[latestInvoice.status] ?? latestInvoice.status}
          {dueDate}.{" "}
          {latestInvoice.hostedInvoiceUrl ? (
            <a
              href={latestInvoice.hostedInvoiceUrl}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              View invoice<span className="sr-only">, opens in a new tab</span>
            </a>
          ) : null}
        </>
      ) : null}
    </p>
  );
}

function SeatTile(props: { label: string; value: ReactNode; hint: string }) {
  // Nested stat tiles: thin border keeps hierarchy inside the Card's border-2.
  return (
    <div className="rounded-lg border px-3 py-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{props.label}</dt>
      <dd className="mt-1 text-sm font-medium">{props.value}</dd>
      <dd className="mt-0.5 text-xs text-muted-foreground">{props.hint}</dd>
    </div>
  );
}

function deriveBillingCardModel({ summary, billing, entitlement }: BillingOverview) {
  // A Stripe customer is enough to open the billing portal; status copy needs a subscription,
  // matching the platform-admin preview (hasStripeSubscription).
  const hasStripeBilling = billing?.provider === "stripe" && Boolean(billing.providerCustomerId);
  const hasStripeSubscription = hasStripeBilling && Boolean(billing?.providerSubscriptionId);
  const hasPaidSubscription = hasStripeBilling && LIVE_SUBSCRIPTION_STATUSES.includes(summary.billingStatus ?? "");
  const isInvoiced = billing?.collectionMethod === "send_invoice";
  const status = describeBillingStatus({
    state: summary.state,
    billingStatus: summary.billingStatus,
    cancelAtPeriodEnd: billing?.cancelAtPeriodEnd,
    currentPeriodEnd: billing?.currentPeriodEnd ?? null,
    grantExpiresAt: entitlement?.expiresAt ?? null,
    graceEndsAt: summary.graceEndsAt,
    isInvoiced,
    hasStripeBilling: hasStripeSubscription,
  });
  // Per-seat billing applies to self-serve subscriptions (current or prospective), not to free
  // grants, suspensions, or invoiced contracts with a negotiated seat quantity.
  const seatBilling: SeatBilling = isInvoiced
    ? "invoiced"
    : summary.state === "free_access" || summary.state === "suspended"
      ? "none"
      : "per_seat";
  const showStart = !hasPaidSubscription && (status.nextAction === "start" || status.nextAction === "continue_checkout");
  return { hasStripeBilling, hasPaidSubscription, isInvoiced, status, seatBilling, showStart };
}

type SeatBilling = "per_seat" | "invoiced" | "none";

function SeatBillingNote(props: { seatBilling: SeatBilling; hasStripeBilling: boolean }) {
  if (props.seatBilling === "invoiced") {
    return (
      <p className="text-sm text-muted-foreground">
        Your invoiced contract sets the number of seats. Invoices and billing details are in Manage billing.
      </p>
    );
  }
  if (props.seatBilling === "none") return null;
  return (
    <p className="text-sm text-muted-foreground">
      Seats are billed for active owners, admins, and members. Pending invitations and reviewers are never billed.
      {props.hasStripeBilling ? " Prices, taxes, invoices, and payment methods are in Manage billing." : ""}
    </p>
  );
}

/** The organization's billing status, seats, and billing actions, in customer terms. */
export function OrganizationBillingCard(props: {
  billingOverview: BillingOverview;
  canManageBilling: boolean;
  pendingBillingAction: "checkout" | "portal" | null;
  /** Outcome of a Checkout the customer just returned from, if any. */
  notice?: ReactNode;
  onStartSubscription: () => void;
  onManageBilling: () => void;
}) {
  const { summary, billing } = props.billingOverview;
  const { hasStripeBilling, hasPaidSubscription, isInvoiced, status, seatBilling, showStart } =
    deriveBillingCardModel(props.billingOverview);
  const isBusy = props.pendingBillingAction !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing</CardTitle>
        <CardDescription>Your company's subscription and seats.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {props.notice}

        <section aria-label="Billing status" className="space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <StatusBadge status={status.tone} label={status.label} showSpinner={false} />
            {status.date ? (
              <p className="text-sm font-semibold">
                {status.date.label} {formatBillingDate(status.date.value)}
              </p>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">{status.description}</p>
        </section>

        <dl className="grid gap-3 sm:grid-cols-3">
          <SeatTile
            label="Editor seats"
            value={summary.billableSeats}
            hint={seatBilling === "per_seat" ? "Owners, admins, and members. Billed per seat." : "Owners, admins, and members."}
          />
          <SeatTile label="Reviewers" value={summary.reviewers} hint="Read-only. Free." />
          {hasPaidSubscription && !isInvoiced ? (
            <SeatTile
              label="Seats on subscription"
              value={summary.billedSeats ?? "-"}
              hint="Always at least one."
            />
          ) : null}
        </dl>

        <SeatBillingNote seatBilling={seatBilling} hasStripeBilling={hasStripeBilling} />

        {hasStripeBilling && isInvoiced && billing ? <InvoicedBillingNotice billing={billing} /> : null}

        {summary.requiresReconciliation ? (
          <p className="text-sm text-muted-foreground">
            Your subscription's seat count is being updated to match your team. Everyone keeps access in the meantime.
          </p>
        ) : null}

        {props.canManageBilling ? (
          <div className="flex flex-col gap-3 sm:flex-row">
            {showStart ? (
              <Button onClick={props.onStartSubscription} disabled={isBusy}>
                {props.pendingBillingAction === "checkout" && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                {status.nextAction === "continue_checkout" ? "Continue checkout" : "Start subscription"}
              </Button>
            ) : null}
            {hasStripeBilling ? (
              <Button variant={showStart ? "outline" : "default"} onClick={props.onManageBilling} disabled={isBusy}>
                {props.pendingBillingAction === "portal" && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                Manage billing
              </Button>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Only owners and admins can change billing. Ask one of them if something here needs attention.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
